// v5
// Path: frontend/src/components/QuickBooksSync/index.jsx
// Inputs: token (JWT authorization string)
// Outputs: React JSX single-page consolidated master-detail interface for QuickBooks integrations
// Summary: Single-page unified management hub for Passport Business Solutions ERP connections to QuickBooks Online, featuring single-line expandable connection rows, in-place connection group editing, group-centric pipeline creation, environmental settings, and interactive data dictionary.

import React, { useState, useEffect, useMemo } from 'react';
import { FieldMappingModal } from './FieldMappingModal';
import { ConnectionDetailsModal } from './ConnectionDetailsModal';
import { ConnectionLogsModal } from './ConnectionLogsModal';
import { DryRunDiffModal } from './DryRunDiffModal';
import { PipelineConfigModal } from './PipelineConfigModal';
import { IntegrationSettingsModal } from './IntegrationSettingsModal';
import { DataDictionaryModal } from './DataDictionaryModal';
import { EditConnectionGroupModal } from './EditConnectionGroupModal';

export default function QuickBooksSync({ token }) {
    const [accounts, setAccounts] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [dbProfiles, setDbProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    const [editingGroup, setEditingGroup] = useState(null);

    // Expandable accordion state (keyed by connection group key)
    const [expandedConnections, setExpandedConnections] = useState({});

    // Modals state
    const [mappingConfig, setMappingConfig] = useState(null);
    const [diffConfig, setDiffConfig] = useState(null);
    const [detailsAccount, setDetailsAccount] = useState(null);
    const [logsAccount, setLogsAccount] = useState(null);
    const [pipelineConfig, setPipelineConfig] = useState(null);
    const [pipelineDefaults, setPipelineDefaults] = useState({});
    const [showPipelineModal, setShowPipelineModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showDictionaryModal, setShowDictionaryModal] = useState(false);
    const [showNewAccountModal, setShowNewAccountModal] = useState(false);
    const [newAccountForm, setNewAccountForm] = useState({
        accountName: '',
        environment: 'sandbox',
        clientId: '',
        clientSecret: '',
        redirectUri: 'https://admin.sbs-roc.com/api/quickbooks/callback'
    });
    const [savingAccount, setSavingAccount] = useState(false);

    // Filter/Search
    const [searchQuery, setSearchQuery] = useState('');
    const [envFilter, setEnvFilter] = useState('ALL');

    const loadData = async () => {
        setLoading(true);
        try {
            const authHeaders = { 'Authorization': `Bearer ${token}` };
            const [accRes, cfgRes, profRes] = await Promise.all([
                fetch('/api/quickbooks/accounts', { headers: authHeaders }),
                fetch('/api/quickbooks/configs', { headers: authHeaders }),
                fetch('/api/quickbooks/database-profiles', { headers: authHeaders })
            ]);

            if (accRes.ok) setAccounts(await accRes.json());
            if (cfgRes.ok) setConfigs(await cfgRes.json());
            if (profRes.ok) setDbProfiles(await profRes.json());
        } catch (err) {
            console.error('[QuickBooksSync] Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();

        // Check OAuth query params
        const params = new URLSearchParams(window.location.search);
        if (params.get('connected') === 'true') {
            setNotification({
                type: 'success',
                message: 'Successfully authorized and connected with QuickBooks Online!'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (params.get('error')) {
            setNotification({
                type: 'error',
                message: `OAuth Authorization Error: ${params.get('error')}`
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [token]);

    // Group configurations into unified Connection entries
    const connections = useMemo(() => {
        const groups = {};

        // 1. Group by configured pipelines
        configs.forEach(cfg => {
            const matchedProfile = dbProfiles.find(p => p.connPrefix === cfg.SourceConnPrefix);
            const profileDisplayName = matchedProfile ? matchedProfile.displayName : (cfg.SourceConnPrefix || 'Local Database Profile');
            const dbName = cfg.SourceDatabaseName || 'pbsdata00';
            const accKey = cfg.AccountID || 'unassigned';
            const groupKey = `${cfg.SourceConnPrefix || 'LOCAL'}__@@__${dbName}__@@__${accKey}`;

            if (!groups[groupKey]) {
                const matchedAccount = accounts.find(a => String(a.AccountID) === String(cfg.AccountID));
                groups[groupKey] = {
                    groupKey,
                    sourceConnPrefix: cfg.SourceConnPrefix || '',
                    profileDisplayName,
                    sourceDatabase: dbName,
                    accountId: cfg.AccountID,
                    account: matchedAccount || null,
                    accountName: cfg.AccountName || matchedAccount?.AccountName || `Account #${cfg.AccountID}`,
                    accountEnvironment: cfg.Environment || matchedAccount?.Environment || 'sandbox',
                    accountConnected: cfg.AccountConnected !== undefined ? cfg.AccountConnected : (matchedAccount?.IsConnected || false),
                    companyName: matchedAccount?.CompanyName || '',
                    pipelines: []
                };
            }

            groups[groupKey].pipelines.push(cfg);
        });

        // 2. Also include any registered QuickBooks accounts that do not have pipelines yet
        accounts.forEach(acc => {
            const hasGroup = Object.values(groups).some(g => String(g.accountId) === String(acc.AccountID));
            if (!hasGroup) {
                const groupKey = `UNLINKED__@@__pbsdata00__@@__${acc.AccountID}`;
                groups[groupKey] = {
                    groupKey,
                    sourceConnPrefix: dbProfiles[0]?.connPrefix || '',
                    profileDisplayName: dbProfiles[0]?.displayName || 'Select Profile',
                    sourceDatabase: 'pbsdata00',
                    accountId: acc.AccountID,
                    account: acc,
                    accountName: acc.AccountName,
                    accountEnvironment: acc.Environment,
                    accountConnected: acc.IsConnected,
                    companyName: acc.CompanyName || '',
                    pipelines: []
                };
            }
        });

        return Object.values(groups);
    }, [configs, accounts, dbProfiles]);

    // Filter connections
    const filteredConnections = useMemo(() => {
        return connections.filter(conn => {
            if (envFilter !== 'ALL' && conn.accountEnvironment !== envFilter) return false;
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchName = conn.accountName.toLowerCase().includes(q);
                const matchDb = conn.sourceDatabase.toLowerCase().includes(q);
                const matchProf = conn.profileDisplayName.toLowerCase().includes(q);
                const matchComp = conn.companyName.toLowerCase().includes(q);
                if (!matchName && !matchDb && !matchProf && !matchComp) return false;
            }
            return true;
        });
    }, [connections, envFilter, searchQuery]);

    const toggleConnectionExpand = (groupKey) => {
        setExpandedConnections(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const handleCreateAccount = async (e) => {
        e.preventDefault();
        setSavingAccount(true);
        try {
            const res = await fetch('/api/quickbooks/accounts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newAccountForm)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to create account');
            }
            setShowNewAccountModal(false);
            setNewAccountForm({
                accountName: '',
                environment: 'sandbox',
                clientId: '',
                clientSecret: '',
                redirectUri: 'https://admin.sbs-roc.com/api/quickbooks/callback'
            });
            loadData();
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setSavingAccount(false);
        }
    };

    const handleEnsureStandardSuite = async (conn) => {
        if (!conn.sourceConnPrefix) {
            alert('Please configure a saved Database Profile first.');
            return;
        }
        if (!conn.sourceDatabase) {
            alert('Source database name required.');
            return;
        }
        if (!conn.accountId) {
            alert('QuickBooks Account required.');
            return;
        }

        setLoading(true);
        try {
            const entities = [
                { type: 'Customers', name: `PBS Customers (${conn.sourceDatabase}) -> QBO` },
                { type: 'Items', name: `PBS Inventory Items (${conn.sourceDatabase}) -> QBO` },
                { type: 'Accounts', name: `PBS Chart of Accounts (${conn.sourceDatabase}) -> QBO` },
                { type: 'Vendors', name: `PBS Vendors (${conn.sourceDatabase}) -> QBO` }
            ];

            for (const ent of entities) {
                const exists = conn.pipelines.some(p => p.EntityType === ent.type);
                if (!exists) {
                    await fetch('/api/quickbooks/configs', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            configName: ent.name,
                            accountId: parseInt(conn.accountId, 10),
                            sourceConnPrefix: conn.sourceConnPrefix,
                            sourceDatabaseName: conn.sourceDatabase,
                            entityType: ent.type,
                            syncDirection: 'PBS_TO_QB',
                            conflictPolicy: 'SOURCE_WINS',
                            isActive: true
                        })
                    });
                }
            }
            loadData();
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRunLiveSyncQuick = async (cfg) => {
        if (!confirm(`Execute LIVE synchronization for ${cfg.EntityType} (${cfg.SourceDatabaseName} -> QuickBooks)?`)) return;
        try {
            const res = await fetch('/api/quickbooks/sync/execute', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    configId: cfg.ConfigID,
                    accountId: cfg.AccountID,
                    sourceConnPrefix: cfg.SourceConnPrefix,
                    sourceDatabaseName: cfg.SourceDatabaseName,
                    entityType: cfg.EntityType,
                    limit: 50
                })
            });
            const data = await res.json();
            if (res.ok) {
                alert(`Sync Completed: ${data.summary}`);
                loadData();
            } else {
                alert(`Sync Error: ${data.error}`);
            }
        } catch (err) {
            alert(`Sync Failed: ${err.message}`);
        }
    };

    const handleDeletePipeline = async (configId) => {
        if (!confirm('Are you sure you want to delete this table sync pipeline?')) return;
        try {
            const res = await fetch(`/api/quickbooks/configs/${configId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) loadData();
        } catch (err) {
            alert(`Delete failed: ${err.message}`);
        }
    };

    const getEntityIcon = (entity) => {
        switch (entity) {
            case 'Customers': return { icon: 'group', table: 'CUSFIL' };
            case 'Items': return { icon: 'inventory_2', table: 'ITMFIL' };
            case 'Accounts': return { icon: 'account_tree', table: 'CHARTF' };
            case 'Vendors': return { icon: 'store', table: 'VENFIL' };
            default: return { icon: 'table_chart', table: 'PBS' };
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 overflow-y-auto p-6 space-y-6">
            {/* Top Header Banner */}
            <div className="flex flex-wrap justify-between items-center gap-4 bg-gradient-to-r from-slate-800 via-slate-800 to-slate-850 p-5 rounded-xl border border-slate-700 shadow-md">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 shadow-inner">
                        <span className="material-symbols-outlined text-[28px]">sync_alt</span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-white tracking-wide">Passport to QuickBooks Sync Hub</h2>
                            <span className="px-2.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full text-xs font-semibold">
                                Live Integration
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                            Single-line connection overview with table sync pipelines, interactive field mappings, and audit diagnostics.
                        </p>
                    </div>
                </div>

                {/* Top Action Buttons */}
                <div className="flex items-center gap-2.5 flex-wrap">
                    <button
                        onClick={() => {
                            setPipelineDefaults({});
                            setPipelineConfig(null);
                            setShowPipelineModal(true);
                        }}
                        className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                    >
                        <span className="material-symbols-outlined text-[16px]">add_link</span>
                        + New Connection Group
                    </button>
                    <button
                        onClick={() => setShowNewAccountModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs font-semibold transition-colors border border-slate-600 shadow-sm"
                    >
                        <span className="material-symbols-outlined text-[16px]">add</span>
                        + Add QuickBooks Account
                    </button>
                    <button
                        onClick={() => setShowSettingsModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition-colors border border-slate-700 shadow-sm"
                        title="Configure default root table names and QB entity destinations"
                    >
                        <span className="material-symbols-outlined text-[16px] text-amber-400">tune</span>
                        Environmental Settings
                    </button>
                    <button
                        onClick={() => setShowDictionaryModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition-colors border border-slate-700 shadow-sm"
                        title="Browse 221 Passport tables and 7,844 fields with custom tags and notes"
                    >
                        <span className="material-symbols-outlined text-[16px] text-emerald-400">menu_book</span>
                        Data Dictionary
                    </button>
                    <button
                        onClick={loadData}
                        disabled={loading}
                        className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors border border-slate-700"
                        title="Refresh all data"
                    >
                        <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>sync</span>
                    </button>
                </div>
            </div>

            {/* Notification Banner */}
            {notification && (
                <div className={`p-4 rounded-xl border text-sm flex items-center justify-between ${notification.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-200' : 'bg-red-950/60 border-red-800 text-red-200'}`}>
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined">
                            {notification.type === 'success' ? 'check_circle' : 'error'}
                        </span>
                        <span>{notification.message}</span>
                    </div>
                    <button onClick={() => setNotification(null)} className="text-slate-400 hover:text-white">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            {/* Filter and Search Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-800/80 p-3.5 rounded-xl border border-slate-700/80 text-xs">
                <div className="flex items-center gap-3 flex-1 min-w-[260px]">
                    <div className="relative flex-1 max-w-md">
                        <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[16px]">search</span>
                        <input
                            type="text"
                            placeholder="Search client database, QB account, or company..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    <div className="flex items-center gap-1.5">
                        <span className="text-slate-400 font-medium">Environment:</span>
                        <select
                            value={envFilter}
                            onChange={e => setEnvFilter(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-200 focus:outline-none focus:border-blue-500"
                        >
                            <option value="ALL">All Environments</option>
                            <option value="sandbox">Sandbox Only</option>
                            <option value="production">Production Only</option>
                        </select>
                    </div>
                </div>

                <div className="text-slate-400 font-medium">
                    Showing <strong>{filteredConnections.length}</strong> active connections ({configs.length} table sync pipelines)
                </div>
            </div>

            {/* Connection Rows List (Single-Line Condensed View) */}
            {filteredConnections.length === 0 ? (
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-12 text-center text-slate-400">
                    <span className="material-symbols-outlined text-5xl text-slate-500 mb-2">account_tree</span>
                    <h3 className="text-lg font-bold text-slate-200">No Integration Connections Configured</h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                        Link a saved client database profile (e.g. `pbsdata00`) to a QuickBooks company to begin synchronizing standard ERP entities.
                    </p>
                    <div className="mt-4 flex justify-center gap-3">
                        <button
                            onClick={() => setShowNewAccountModal(true)}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            + Setup QuickBooks Account
                        </button>
                        <button
                            onClick={() => {
                                setPipelineDefaults({});
                                setPipelineConfig(null);
                                setShowPipelineModal(true);
                            }}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            + Create First Pipeline
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredConnections.map(conn => {
                        const isExpanded = !!expandedConnections[conn.groupKey];
                        const isSandbox = conn.accountEnvironment === 'sandbox';
                        const isConnected = conn.accountConnected;
                        const activePipelinesCount = conn.pipelines.filter(p => p.IsActive).length;

                        return (
                            <div
                                key={conn.groupKey}
                                className={`relative bg-slate-800 border rounded-xl overflow-hidden shadow-md transition-all ${isExpanded ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-slate-700 hover:border-slate-600'}`}
                            >
                                {/* Yellow Cornertag for Sandbox Accounts */}
                                {isSandbox && (
                                    <div className="absolute top-0 right-0 overflow-hidden w-20 h-20 pointer-events-none z-10">
                                        <div className="bg-amber-400 text-slate-950 text-[9px] font-black tracking-widest text-center py-0.5 shadow-md transform rotate-45 translate-x-6 translate-y-3 uppercase">
                                            SANDBOX
                                        </div>
                                    </div>
                                )}

                                {/* Single-Line Main Row */}
                                <div
                                    onClick={() => toggleConnectionExpand(conn.groupKey)}
                                    className="p-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer select-none bg-slate-800 hover:bg-slate-750 transition-colors"
                                >
                                    {/* Left: Indicator, Client DB, QB Account */}
                                    <div className="flex items-center gap-3.5 min-w-[280px] flex-1">
                                        {/* Status Pulsing Dot */}
                                        <div
                                            className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 border border-slate-700"
                                            title={isConnected ? 'Connected & Authorized' : 'Authorization Required'}
                                        >
                                            <span className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)] animate-pulse' : 'bg-amber-400'}`}></span>
                                        </div>

                                        {/* Client / Database Name */}
                                        <div className="flex items-center gap-2">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-bold text-white font-mono">
                                                        {conn.sourceDatabase}
                                                    </span>
                                                    <span className="px-2 py-0.5 text-[10px] rounded bg-slate-900 text-slate-300 border border-slate-700 font-mono truncate max-w-[220px]">
                                                        {conn.profileDisplayName}
                                                    </span>
                                                    <span className="text-slate-500">&rarr;</span>
                                                    <span className="text-sm font-bold text-emerald-300">
                                                        {conn.accountName}
                                                    </span>
                                                    {conn.companyName && (
                                                        <span className="text-xs text-slate-400 font-medium">
                                                            ({conn.companyName})
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-slate-400 mt-0.5">
                                                    {conn.pipelines.length === 0 ? (
                                                        <span className="text-amber-400">No table pipelines linked</span>
                                                    ) : (
                                                        <span>
                                                            <strong>{activePipelinesCount}</strong> of <strong>{conn.pipelines.length}</strong> table syncs active &bull; {conn.pipelines.map(p => p.EntityType).join(', ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Quick Actions & Chevron */}
                                    <div className="flex items-center gap-2 z-20" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => setEditingGroup(conn)}
                                            className="px-2.5 py-1 bg-slate-900 hover:bg-slate-700 text-slate-200 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                            title="Edit shared SQL Server instance, network interface, database, and QB account for this group"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-amber-400">tune</span>
                                            Edit Connection
                                        </button>

                                        <button
                                            onClick={() => setDetailsAccount(conn.account || { AccountID: conn.accountId, AccountName: conn.accountName, Environment: conn.accountEnvironment, IsConnected: conn.accountConnected })}
                                            className="px-2.5 py-1 bg-slate-900 hover:bg-slate-700 text-slate-300 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                            title="View Client ID, Token Expiration & Re-Auth"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-emerald-400">key</span>
                                            Credentials
                                        </button>

                                        <button
                                            onClick={() => setLogsAccount({ accountId: conn.accountId, title: `${conn.sourceDatabase} ➔ ${conn.accountName}` })}
                                            className="px-2.5 py-1 bg-slate-900 hover:bg-slate-700 text-slate-300 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                            title="View Execution Audit Logs"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-blue-400">receipt_long</span>
                                            Audit Logs
                                        </button>

                                        <button
                                            onClick={() => toggleConnectionExpand(conn.groupKey)}
                                            className="p-1 text-slate-400 hover:text-white rounded hover:bg-slate-700 transition-colors ml-1"
                                            title={isExpanded ? 'Collapse' : 'Expand Details'}
                                        >
                                            <span className="material-symbols-outlined text-[20px]">
                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Details Panel */}
                                {isExpanded && (
                                    <div className="border-t border-slate-700/80 bg-slate-900/50 p-4 space-y-4">
                                        {/* Table Sync Pipelines Header & Sub-Actions */}
                                        <div className="flex flex-wrap justify-between items-center gap-2">
                                            <div className="text-xs font-bold text-slate-300 flex items-center gap-1.5 uppercase tracking-wider">
                                                <span className="material-symbols-outlined text-[16px] text-blue-400">table_chart</span>
                                                Linked Table Sync Pipelines ({conn.pipelines.length})
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setEditingGroup(conn)}
                                                    className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                                    title="Edit shared SQL Server instance, network interface, database, and QB account for this group"
                                                >
                                                    <span className="material-symbols-outlined text-[14px] text-amber-400">tune</span>
                                                    Edit Connection
                                                </button>
                                                <button
                                                    onClick={() => handleEnsureStandardSuite(conn)}
                                                    disabled={loading}
                                                    className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs font-medium transition-colors flex items-center gap-1 border border-slate-700"
                                                    title="Auto-create missing standard tables (Customers, Items, Accounts, Vendors)"
                                                >
                                                    <span className="material-symbols-outlined text-[14px] text-amber-400">auto_fix_high</span>
                                                    Ensure 4-Table Suite
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setPipelineDefaults({
                                                            isGroupAdd: true,
                                                            sourceConnPrefix: conn.sourceConnPrefix,
                                                            sourceDatabaseName: conn.sourceDatabase,
                                                            accountId: conn.accountId
                                                        });
                                                        setPipelineConfig(null);
                                                        setShowPipelineModal(true);
                                                    }}
                                                    className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold transition-colors flex items-center gap-1 shadow-sm"
                                                    title={`Add a new table sync to ${conn.sourceDatabase}`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">add</span>
                                                    + Add Table Sync
                                                </button>
                                            </div>
                                        </div>

                                        {/* Table Syncs List */}
                                        {conn.pipelines.length === 0 ? (
                                            <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 text-center text-slate-400 text-xs">
                                                No table sync pipelines created for this connection yet. Click <strong>Ensure 4-Table Suite</strong> to automatically generate standard pipelines for Customers, Items, Accounts, and Vendors.
                                            </div>
                                        ) : (
                                            <div className="border border-slate-700/80 rounded-lg overflow-hidden bg-slate-800/80 shadow-inner">
                                                <table className="w-full text-left text-xs text-slate-300">
                                                    <thead className="bg-slate-900/90 text-slate-400 font-semibold border-b border-slate-700">
                                                        <tr>
                                                            <th className="p-3">Table / Entity</th>
                                                            <th className="p-3">Direction & Policy</th>
                                                            <th className="p-3">Status</th>
                                                            <th className="p-3">Last Synced</th>
                                                            <th className="p-3 text-right">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-700/60">
                                                        {conn.pipelines.map(cfg => {
                                                            const meta = getEntityIcon(cfg.EntityType);
                                                            return (
                                                                <tr key={cfg.ConfigID} className="hover:bg-slate-750/70 transition-colors">
                                                                    <td className="p-3">
                                                                        <div className="flex items-center gap-2.5">
                                                                            <div className="w-7 h-7 rounded bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
                                                                                <span className="material-symbols-outlined text-[16px]">{meta.icon}</span>
                                                                            </div>
                                                                            <div>
                                                                                <div className="font-bold text-white">{cfg.EntityType}</div>
                                                                                <div className="text-[11px] font-mono text-slate-400">dbo.{meta.table} &bull; {cfg.ConfigName}</div>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="p-3">
                                                                        <div className="font-medium text-blue-300">{cfg.SyncDirection.replace(/_/g, ' ')}</div>
                                                                        <div className="text-[10px] text-slate-400">Policy: {cfg.ConflictPolicy}</div>
                                                                    </td>
                                                                    <td className="p-3">
                                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${cfg.IsActive ? 'bg-emerald-950 text-emerald-300 border-emerald-800' : 'bg-slate-900 text-slate-500 border-slate-700'}`}>
                                                                            {cfg.IsActive ? 'Active' : 'Paused'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="p-3 font-mono text-slate-400 text-[11px]">
                                                                        {cfg.LastSyncAt ? new Date(cfg.LastSyncAt).toLocaleString() : 'Never'}
                                                                    </td>
                                                                    <td className="p-3 text-right">
                                                                        <div className="flex items-center justify-end gap-1.5">
                                                                            {/* Field Mappings button */}
                                                                            <button
                                                                                onClick={() => setMappingConfig(cfg)}
                                                                                className="px-2 py-1 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 border border-indigo-500/40 rounded text-[11px] font-semibold flex items-center gap-1 transition-colors"
                                                                                title="Update & Assign Field Mappings"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[14px]">sync_alt</span>
                                                                                Field Mappings
                                                                            </button>

                                                                            {/* Inspect / Diff button */}
                                                                            <button
                                                                                onClick={() => setDiffConfig(cfg)}
                                                                                className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-[11px] font-semibold flex items-center gap-1 transition-colors shadow-sm"
                                                                                title="Inspect PBS source & Dry-Run Diff"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[14px]">preview</span>
                                                                                Inspect / Diff
                                                                            </button>

                                                                            {/* Quick Sync */}
                                                                            <button
                                                                                onClick={() => handleRunLiveSyncQuick(cfg)}
                                                                                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-bold flex items-center gap-1 transition-colors shadow-sm"
                                                                                title="Execute Live Sync"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[14px]">bolt</span>
                                                                                Sync
                                                                            </button>

                                                                             {/* Edit */}
                                                                            <button
                                                                                onClick={() => {
                                                                                    setPipelineConfig(cfg);
                                                                                    setPipelineDefaults({});
                                                                                    setShowPipelineModal(true);
                                                                                }}
                                                                                className="p-1 text-slate-400 hover:text-blue-400 transition-colors rounded"
                                                                                title="Edit Pipeline"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[16px]">edit</span>
                                                                            </button>

                                                                            {/* Delete */}
                                                                            <button
                                                                                onClick={() => handleDeletePipeline(cfg.ConfigID)}
                                                                                className="p-1 text-slate-400 hover:text-red-400 transition-colors rounded"
                                                                                title="Delete Pipeline"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Field Mapping Modal */}
            {mappingConfig && (
                <FieldMappingModal
                    token={token}
                    config={mappingConfig}
                    onClose={() => setMappingConfig(null)}
                    onSaved={loadData}
                />
            )}

            {/* Dry Run Diff & Live Sync Modal */}
            {diffConfig && (
                <DryRunDiffModal
                    token={token}
                    config={diffConfig}
                    onClose={() => setDiffConfig(null)}
                    onSyncCompleted={loadData}
                />
            )}

            {/* Connection Credentials & Token Diagnostics Modal */}
            {detailsAccount && (
                <ConnectionDetailsModal
                    token={token}
                    account={detailsAccount}
                    onClose={() => setDetailsAccount(null)}
                    onAccountUpdated={loadData}
                />
            )}

            {/* Connection Audit Logs Modal */}
            {logsAccount && (
                <ConnectionLogsModal
                    token={token}
                    accountId={logsAccount.accountId}
                    connectionTitle={logsAccount.title}
                    onClose={() => setLogsAccount(null)}
                />
            )}

            {/* Pipeline Configuration Modal (Create or Edit) */}
            {showPipelineModal && (
                <PipelineConfigModal
                    token={token}
                    config={pipelineConfig}
                    accounts={accounts}
                    dbProfiles={dbProfiles}
                    initialDefaults={pipelineDefaults}
                    onClose={() => {
                        setShowPipelineModal(false);
                        setPipelineConfig(null);
                        setPipelineDefaults({});
                    }}
                    onSaved={loadData}
                />
            )}

            {/* Environmental Integration Settings Modal */}
            {showSettingsModal && (
                <IntegrationSettingsModal
                    token={token}
                    onClose={() => setShowSettingsModal(false)}
                    onSettingsUpdated={loadData}
                />
            )}

            {/* Passport Data Dictionary & Field Tagger Modal */}
            {showDictionaryModal && (
                <DataDictionaryModal
                    token={token}
                    onClose={() => setShowDictionaryModal(false)}
                />
            )}

            {/* Add QuickBooks Account Modal */}
            {showNewAccountModal && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full p-6 shadow-2xl space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-emerald-400">domain_add</span>
                                Add QuickBooks Online Account
                            </h3>
                            <button onClick={() => setShowNewAccountModal(false)} className="text-slate-400 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <form onSubmit={handleCreateAccount} className="space-y-3 text-xs">
                            <div>
                                <label className="block text-slate-300 font-medium mb-1">Account Label *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Acme Corp QuickBooks"
                                    value={newAccountForm.accountName}
                                    onChange={e => setNewAccountForm({ ...newAccountForm, accountName: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-300 font-medium mb-1">Environment *</label>
                                <select
                                    value={newAccountForm.environment}
                                    onChange={e => setNewAccountForm({ ...newAccountForm, environment: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white focus:outline-none focus:border-blue-500"
                                >
                                    <option value="sandbox">Sandbox (Development)</option>
                                    <option value="production">Production (Live)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-slate-300 font-medium mb-1">Client ID *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Intuit App Client ID"
                                    value={newAccountForm.clientId}
                                    onChange={e => setNewAccountForm({ ...newAccountForm, clientId: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-300 font-medium mb-1">Client Secret</label>
                                <input
                                    type="password"
                                    placeholder="Intuit App Client Secret"
                                    value={newAccountForm.clientSecret}
                                    onChange={e => setNewAccountForm({ ...newAccountForm, clientSecret: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-300 font-medium mb-1">Redirect URI</label>
                                <input
                                    type="text"
                                    value={newAccountForm.redirectUri}
                                    onChange={e => setNewAccountForm({ ...newAccountForm, redirectUri: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-3 border-t border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => setShowNewAccountModal(false)}
                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingAccount}
                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded text-xs font-bold transition-colors"
                                >
                                    {savingAccount ? 'Saving...' : 'Create Account'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Connection Group Modal */}
            {editingGroup && (
                <EditConnectionGroupModal
                    token={token}
                    group={editingGroup}
                    dbProfiles={dbProfiles}
                    accounts={accounts}
                    onClose={() => setEditingGroup(null)}
                    onSaved={() => {
                        setEditingGroup(null);
                        loadData();
                    }}
                />
            )}
        </div>
    );
}
